import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Button, ContactPhone, Loading } from '@tradingblock/components';
import { getIdNumber } from '@tradingblock/api';
import { apiTokenSelector, useStateSelector } from '../../../data/global/dataSelectors';
import { entitySelector } from '../../../data/global/selectors/admin/cashieringSelectors';
import { CashieringTransferDetails } from './components/CashieringTransferDetails';
import { CashieringReviewForm } from './components/CashieringReviewForm';
import { CashieringRelationshipDetails } from './components/CashieringRelationshipDetails';
import { useDispatcher } from '../../../data/global/hooks';
import { AdminCashieringActions } from '../../../data/global/actions/admin/AdminCashieringActions';
import { CashieringDetailError } from './components/CashieringDetailError';
import { CashieringEntityNotes } from './components/details/CashieringEntityNotes';
import { CashieringRelationshipStatus } from './components/CashieringRelationshipStatus';
import { CashieringTransferStatus } from './components/CashieringTransferStatus';
import { CashieringTransferInstructionDetails } from './components/CashieringTransferInstructionDetails';
import { CashieringTransferInstructionStatus } from './components/CashieringTransferInstructionStatus';
import { FilterForAccount } from './components/FilterForAccount';
import { accountDetailsSelectors } from '../../../data/global/selectors/admin/accountSelectors';
import { useFeedActions } from '../../../data/feed/useFeedActions';
import { FeedCashieringUpdate } from '@tradingblock/types';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

// Styled Components
const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  padding: 0;
`;

const SectionCard = styled.div`
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

/** Keeps Review & Actions visible while scrolling long detail content in the left column */
const StickyReviewSectionCard = styled(SectionCard)`
  overflow: visible;
  position: sticky;
  top: var(--gap);
  align-self: flex-start;
  z-index: 2;
`;

const SectionHeader = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  border-bottom: 1px solid var(--lighten);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const SectionContent = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gap);
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .loading-text {
    margin-left: 0.75rem;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.75rem;
  }
`;

const ErrorContainer = styled.div`
  background: rgba(236, 77, 92, 0.1);
  border: 1px solid rgba(236, 77, 92, 0.3);
  border-radius: var(--radius);
  padding: calc(var(--gap) / 2);
  margin: calc(var(--gap) / 2) 0;
  box-shadow: 0 2px 8px rgba(236, 77, 92, 0.1);
`;

const StatusMessage = styled.div`
  background: rgba(25, 186, 180, 0.1);
  border: 1px solid rgba(25, 186, 180, 0.3);
  border-radius: var(--radius);
  padding: calc(var(--gap) / 2);
  margin: calc(var(--gap) / 2) 0;
  box-shadow: 0 2px 8px rgba(25, 186, 180, 0.1);

  p {
    margin: 0;
    color: var(--teal);
    font-size: 0.75rem;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: var(--gap);
  background: transparent;
  border: none;
  padding: 0;
  box-shadow: none;
`;

// Main layout grid - left for details, right for actions
const MainDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--gap);
  margin-bottom: var(--gap);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Left column layout - horizontal flow for detail sections
const DetailsColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--gap);
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  /* Ensure sections don't stretch unnecessarily */
  > * {
    height: fit-content;
  }
`;

// For sections that should span full width
const FullWidthSection = styled.div`
  grid-column: 1 / -1;
`;

// Right column layout - vertical stack for actions
const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap);
`;

const useCashieringData = (
  entityType: 'relationships' | 'transfers' | 'wireInstructions',
  id: string,
  accountId: number,
  latestCashieringUpdateFeedDataFromWebsocket?: FeedCashieringUpdate | null
) => {
  const { dispatch } = useDispatcher();

  useEffect(() => {
    if (entityType === 'relationships') {
      dispatch(AdminCashieringActions.cashieringRelationshipDetailRequest({ accountId, id }));
    } else if (entityType === 'transfers') {
      dispatch(AdminCashieringActions.cashieringTransferDetailRequest({ accountId, id }));
    } else if (entityType === 'wireInstructions') {
      dispatch(AdminCashieringActions.cashieringWireInstructionsDetailsRequest({ accountId, id }));
    }
  }, [accountId, dispatch, entityType, id, latestCashieringUpdateFeedDataFromWebsocket]);
  const entityInfo = useStateSelector(s => entitySelector.info(s, { id, type: entityType }));

  return { ...entityInfo, accountId };
};

// Add a simple Toast component
const ToastContainer = styled.div`
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  min-width: 320px;
  max-width: 90vw;
`;
const Toast = styled.div<{ success?: boolean }>`
  background: ${({ success }) => (success ? 'var(--teal-light)' : 'var(--salmon)')};
  color: ${({ success }) => (success ? 'var(--blue-dark)' : '#fff')};
  padding: 1rem 2rem;
  border-radius: var(--radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 1em;
  margin-bottom: 1rem;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  }
`;
const ToastClose = styled.button`
  background: none;
  border: none;
  color: inherit;
  font-size: 1.5rem;
  margin-left: 1em;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.7;
  }
`;

export const AdminCashieringDetail: React.FunctionComponent<{
  id: string;
  entityType: string;
  accountId: number;
  onViewAllEntries?: (accountNumber: string) => void;
  onSaveComplete?: (toast: { success: boolean; message: string }) => void;
}> = ({ id, entityType, accountId, onViewAllEntries, onSaveComplete }) => {
  const isRelationship = ['AchRelationship', 'relationship', 'relationships']
    .map(v => v.toLocaleLowerCase())
    .includes(entityType.toLocaleLowerCase());
  const isTransferWireInstruction = ['TransferWireInstruction', 'transferWireInstruction', 'wireInstructions']
    .map(v => v.toLocaleLowerCase())
    .includes(entityType.toLocaleLowerCase());

  const type = isRelationship ? 'relationships' : isTransferWireInstruction ? 'wireInstructions' : 'transfers';
  const idValue = getIdNumber(id);

  const accountDetails = useStateSelector(s => accountDetailsSelectors.data(s, accountId));
  const accountStatus = accountDetails ? accountDetails.accountStatus : undefined;
  const targetedAccountStatuses =
    accountStatus === 'Rejected' || accountStatus === 'Closed' || accountStatus === 'Closed Never Funded';
  const rejectMsg =
    'Please note that cashiering actions are disabled at the moment due to the account status. Please contact the support team for more information.';

  const [editNote, setEditNote] = useState(false);

  const [saveState, setSaveState] = useState<'saving' | 'saved'>();
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  useEffect(() => {
    if (saveState === 'saved') {
      // Call the callback to notify parent that save is complete, passing a success message
      if (onSaveComplete) {
        onSaveComplete({ success: true, message: 'Action completed successfully.' });
      }
    }
  }, [saveState, onSaveComplete]);

  const feedProviderStatus = useStateSelector(s => s.cashieringFeedProvider.status);
  const apiToken = useStateSelector(apiTokenSelector);
  const { subscribeToCashieringUpdates, unsubscribeFromCashieringUpdates } = useFeedActions(apiToken ? apiToken : '');
  const latestCashieringUpdateFeedDataFromWebsocket = useStateSelector(
    s => s.private.cashiering.latestCashieringUpdateDataFromWebsocket
  );
  const { isFetching, isSaving, isErrored, error } = useCashieringData(type, id, accountId, latestCashieringUpdateFeedDataFromWebsocket);


  // Only show the toast for errors (not for success, which is now handled by parent)
  useEffect(() => {
    if (isErrored && error) {
      setToast({ message: error.message || 'An error occurred. Please try again.', success: false });
    }
  }, [isErrored, error]);

  useEffect(() => {
    //only track 'saving' state if we aren't saving the note
    if (isSaving && !editNote) {
      setSaveState('saving');
    } else if (saveState === 'saving') {
      if (!isErrored) {
        // save completed successfully
        setSaveState('saved');
      } else {
        // clear save state if errored
        setSaveState(undefined);
      }
    }
  }, [isSaving, saveState, isErrored]);

  // On mount we are subscribing to cashiering update events related to the account ID and on unmount we are unsubscribing from them.
  useEffect(() => {
    if (feedProviderStatus === 'open' && apiToken && accountId) {
      subscribeToCashieringUpdates([accountId]);
    }

    // Don't unsubscribe on unmount - let the subscription management handle this
    // This prevents unsubscribing when navigating back to the table
  }, [feedProviderStatus, apiToken, accountId]);

  // redirect back to list after saving
  if (saveState === 'saved') {
    // Call the callback to notify parent that save is complete
    if (onSaveComplete) {
      onSaveComplete({ success: true, message: 'Action completed successfully.' });
    }
    return null; // Don't redirect, let parent handle navigation
  }
  return (
    <>
      {toast &&
        createPortal(
          <ToastContainer>
            <Toast success={toast.success} role="alert" aria-live="assertive">
              {toast.success ? <i className="fal fa-check-circle" /> : <i className="fal fa-exclamation-triangle" />}
              {toast.message}
              <ToastClose onClick={() => setToast(null)} aria-label="Close notification">
                &times;
              </ToastClose>
            </Toast>
          </ToastContainer>,
          document.body
        )}
      <DetailWrapper>
        {!isFetching && (
          <FilterContainer>
            <FilterForAccount accountId={accountId} onViewAllEntries={onViewAllEntries} />
          </FilterContainer>
        )}

        {isFetching && (
          <LoadingContainer>
            <Loading />
            <span className="loading-text">Fetching details...</span>
          </LoadingContainer>
        )}

        {/* Main two-column layout: left = details, right = actions */}
        {!isFetching && (
          <MainDetailGrid>
            <DetailsColumn>
              {/* Transfer Details - now uses internal grid layout */}
              {!isRelationship && !isTransferWireInstruction && (
                <SectionCard>
                  <SectionHeader>
                    <h3>
                      <i className="fal fa-exchange-alt" style={{ marginRight: '8px' }}></i>
                      Transfer Details
                    </h3>
                  </SectionHeader>
                  <SectionContent>
                    <CashieringTransferDetails
                      id={idValue}
                      accountId={_.toInteger(accountId)}
                      isFetching={isFetching}
                      useGridLayout={true} // Enable grid layout
                    />
                  </SectionContent>
                </SectionCard>
              )}

              {/* Relationship details - full width */}
              {isRelationship && (
                <FullWidthSection>
                  <SectionCard>
                    <SectionContent>
                      <CashieringRelationshipDetails id={idValue} accountId={accountId} isFetching={isFetching} />
                    </SectionContent>
                  </SectionCard>
                </FullWidthSection>
              )}

              {/* Transfer Instruction details - full width */}
              {!isRelationship && isTransferWireInstruction && (
                <FullWidthSection>
                  <SectionCard>
                    <SectionHeader>
                      <h3>
                        <i className="fal fa-file-invoice" style={{ marginRight: '8px' }}></i>
                        Transfer Instruction Details
                      </h3>
                    </SectionHeader>
                    <SectionContent>
                      <CashieringTransferInstructionDetails
                        id={idValue}
                        accountId={_.toInteger(accountId)}
                        isFetching={isFetching}
                      />
                    </SectionContent>
                  </SectionCard>
                </FullWidthSection>
              )}
            </DetailsColumn>
            <ActionsColumn>
              {/* Review & Actions at the top of right column */}
              {!editNote && !targetedAccountStatuses && (
                <StickyReviewSectionCard>
                  <SectionHeader>
                    <h3>
                      <i className="fal fa-tools" style={{ marginRight: '8px' }}></i>
                      Review & Actions
                    </h3>
                  </SectionHeader>
                  <SectionContent>
                    <CashieringReviewForm
                      id={idValue}
                      type={type}
                      accountId={accountId}
                      onClose={onViewAllEntries ? () => onViewAllEntries('') : undefined}
                    />
                  </SectionContent>
                </StickyReviewSectionCard>
              )}
              {/* Notes below Review & Actions */}
              {!isErrored && !targetedAccountStatuses && (
                <SectionCard>
                  <SectionHeader>
                    <h3>
                      <i className="fal fa-sticky-note" style={{ marginRight: '8px' }}></i>
                      Notes
                    </h3>
                  </SectionHeader>
                  <SectionContent>
                    <div className="screen-body-section">
                      <CashieringEntityNotes
                        accountId={accountId}
                        id={idValue}
                        type={type}
                        onEditToggle={setEditNote}
                      />
                    </div>
                  </SectionContent>
                </SectionCard>
              )}
              {/* Relationship/Transfer/Instruction Status below Notes */}
              {isRelationship && (
                <SectionCard>
                  <SectionHeader>
                    <h3>
                      <i className="fal fa-link" style={{ marginRight: '8px' }}></i>
                      Relationship Status
                    </h3>
                  </SectionHeader>
                  <SectionContent>
                    <CashieringRelationshipStatus id={idValue} accountId={accountId} />
                  </SectionContent>
                </SectionCard>
              )}
              {!isRelationship && !isTransferWireInstruction && (
                <SectionCard>
                  <SectionHeader>
                    <h3>
                      <i className="fal fa-sync-alt" style={{ marginRight: '8px' }}></i>
                      Transfer Status
                    </h3>
                  </SectionHeader>
                  <SectionContent>
                    <CashieringTransferStatus id={idValue} accountId={_.toInteger(accountId)} />
                  </SectionContent>
                </SectionCard>
              )}
              {!isRelationship && isTransferWireInstruction && (
                <SectionCard>
                  <SectionHeader>
                    <h3>
                      <i className="fal fa-clipboard-list" style={{ marginRight: '8px' }}></i>
                      Transfer Instruction Status
                    </h3>
                  </SectionHeader>
                  <SectionContent>
                    <CashieringTransferInstructionStatus id={idValue} accountId={_.toInteger(accountId)} />
                  </SectionContent>
                </SectionCard>
              )}
            </ActionsColumn>
          </MainDetailGrid>
        )}

        {onViewAllEntries && (
          <div className="screen-footer screen-footer-justified">
            <Button
              className="btn btn-outline-light"
              onClick={() => onViewAllEntries('')}
              style={{ minWidth: 120, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5em' }}
            >
              <i className="fal fa-arrow-left" /> Back to List
            </Button>
          </div>
        )}

        {/* Full-width sections below the main grid */}
        {!isFetching && (
          <>
            {isErrored && (
              <ErrorContainer>
                <CashieringDetailError error={error} />
              </ErrorContainer>
            )}

            {targetedAccountStatuses && (
              <StatusMessage>
                <p>
                  {rejectMsg} <ContactPhone />
                </p>
              </StatusMessage>
            )}
          </>
        )}
      </DetailWrapper>
    </>
  );
};
